(() => {
  if (window.FF_CART_PROGRESS_INITIALIZED) return;
  window.FF_CART_PROGRESS_INITIALIZED = true;

  const BASE_CURRENCY = 'EUR';
  const WIDGET_SELECTOR = '[data-ff-cart-progress]';

  const toNumber = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    const number = Number(normalized);
    return Number.isFinite(number) ? number : 0;
  };

  const getShopifyRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const fetchCart = async () => {
    try {
      const response = await fetch(`${getShopifyRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    }
  };

  const getActiveCurrency = (widget, cart) => (
    cart?.currency ||
    window.Shopify?.currency?.active ||
    widget.dataset.currentCurrency ||
    BASE_CURRENCY
  ).toUpperCase();

  const formatMoney = (cents, currency) => {
    const locale = document.documentElement.lang || undefined;
    const amount = cents / 100;
    if (!Number.isFinite(amount)) return '';

    const isWholeAmount = Math.abs(amount - Math.round(amount)) < 0.001;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: isWholeAmount ? 0 : 2,
        maximumFractionDigits: isWholeAmount ? 0 : 2
      }).format(amount);
    } catch (_error) {
      return isWholeAmount ? `${Math.round(amount)} ${currency}` : `${amount.toFixed(2)} ${currency}`;
    }
  };

  const convertAmount = (amount, fromCurrency, toCurrency) => {
    if (!Number.isFinite(amount) || amount <= 0) return null;
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return amount;

    if (window.Currency && typeof window.Currency.convert === 'function') {
      const converted = Number(window.Currency.convert(amount, fromCurrency, toCurrency));
      if (Number.isFinite(converted) && converted > 0) return converted;
    }

    if (fromCurrency === BASE_CURRENCY) {
      const shopifyRate = Number(window.Shopify?.currency?.rate);
      if (Number.isFinite(shopifyRate) && shopifyRate > 0) return amount * shopifyRate;
    }

    return null;
  };

  const fillTemplate = (template, amount) => String(template || '').replace('__AMOUNT__', amount);

  const updateWidget = (widget, cart) => {
    const currency = getActiveCurrency(widget, cart);
    const subtotal = Number(cart?.total_price ?? widget.dataset.shippingProgressSubtotal ?? 0);
    const configuredAmount = toNumber(widget.dataset.freeShippingThreshold);
    const configuredCurrency = String(widget.dataset.freeShippingCurrency || currency).toUpperCase();
    const convertedAmount = convertAmount(configuredAmount, configuredCurrency, currency);

    const shippingLine = widget.querySelector('[data-ff-progress-shipping]');
    const meter = widget.querySelector('[data-ff-progress-meter]');
    const fill = widget.querySelector('[data-ff-progress-fill]');

    if (!convertedAmount) {
      if (meter) meter.hidden = true;
      return;
    }

    const thresholdCents = Math.round(convertedAmount * 100);
    const thresholdMoney = formatMoney(thresholdCents, currency);
    const progressPercent = thresholdCents > 0
      ? Math.min(100, Math.max(0, (subtotal / thresholdCents) * 100))
      : 0;

    if (shippingLine) {
      shippingLine.textContent = fillTemplate(widget.dataset.shippingStaticTemplate, thresholdMoney).trim();
    }
    if (meter) {
      meter.hidden = false;
      meter.style.setProperty('--ff-progress', `${progressPercent}%`);
    }
    if (fill) fill.style.width = `${progressPercent}%`;
  };

  let scheduled = false;
  let updateToken = 0;

  const updateAll = async () => {
    scheduled = false;
    const token = ++updateToken;
    const widgets = Array.from(document.querySelectorAll(WIDGET_SELECTOR));
    if (!widgets.length) return;

    const cart = await fetchCart();
    if (token !== updateToken) return;
    widgets.forEach((widget) => updateWidget(widget, cart));
  };

  const scheduleUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(updateAll, 100);
  };

  document.addEventListener('DOMContentLoaded', scheduleUpdate);
  document.addEventListener('cart:change', scheduleUpdate);
  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);

  document.addEventListener('click', (event) => {
    if (
      event.target.closest('quantity-selector') ||
      event.target.closest('line-item-quantity') ||
      event.target.closest('[name="updates[]"]') ||
      event.target.closest('.quantity-selector__button') ||
      event.target.closest('[aria-label*="Remove"]')
    ) scheduleUpdate();
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(WIDGET_SELECTOR) || node.querySelector?.(WIDGET_SELECTOR)) {
          scheduleUpdate();
          return;
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  scheduleUpdate();
})();
